import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/axiosConfig';

// Types
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'user' | 'admin' | 'company' | 'recruiter' | 'bdm' | 'project_trainer' | 'crm';
  designation?: 'team_executive' | 'team_leader' | 'district_head' | 'state_head' | 'regional_head';
  region?: 'north' | 'south' | 'east' | 'west' | 'central';
  skills?: Array<{
    _id: string;
    name: string;
    description?: string;
  }>;
  skillsDescription?: string;
  qualification?: string;
  experience?: string;
  company?: string;
  manager?: string;
  subordinates?: string[];
  personalDetails?: {
    dateOfBirth?: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      district?: string;
      pincode?: string;
      country?: string;
    };
    qualifications?: Array<{
      degree: string;
      institution: string;
      year: number;
      percentage: number;
    }>;
    panNumber?: string;
    aadhaarNumber?: string;
  };
  bankDetails?: {
    accountNumber: string;
    ifscCode: string;
    bankName: string;
    branchName: string;
    accountHolderName: string;
    verified: boolean;
  };
  earnings?: {
    totalEarnings: number;
    pendingAmount: number;
    paidAmount: number;
    referralEarnings: number;
    validatedEarnings: number;
    withdrawnAmount: number;
    currentWallet: number;
    verifiedAmount: number;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Company {
  id: string;
  companyName: string;
  email: string;
  phone: string;
  role: 'company';
  industry?: string;
  size?: string;
  website?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Admin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'admin';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Staff {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'recruiter' | 'bdm' | 'project_trainer' | 'crm';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AuthUser = User | Company | Admin | Staff;

// Type guards
export const isUser = (user: AuthUser): user is User => user.role === 'user';
export const isCompany = (user: AuthUser): user is Company => user.role === 'company';
export const isAdmin = (user: AuthUser): user is Admin => user.role === 'admin';
export const isStaff = (user: AuthUser): user is Staff => ['recruiter', 'bdm', 'project_trainer', 'crm'].includes(user.role);

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Load initial state from localStorage
const loadAuthState = (): AuthState => {
  try {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    return {
      user,
      token,
      isAuthenticated: !!(token && user),
      isLoading: false,
      error: null,
    };
  } catch (error) {
    console.error('Error loading auth state from localStorage:', error);
    return {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    };
  }
};

const initialState: AuthState = loadAuthState();

// Async thunks
export const register = createAsyncThunk(
  'auth/register',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);
      return { user: response.data.user, token: response.data.token };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message ||
        (error.response?.data?.errors && Array.isArray(error.response.data.errors)
          ? error.response.data.errors.map((e: any) => e.msg || e.message).join(', ')
          : 'Registration failed');
      return rejectWithValue(errorMessage);
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const { token, user } = response.data;

      localStorage.setItem('token', token);

      return { token, user };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      localStorage.removeItem('token');
      return true;
    } catch (error: any) {
      return rejectWithValue('Logout failed');
    }
  }
);

export const getMe = createAsyncThunk(
  'auth/getMe',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue('No token found');
      }

      const response = await api.get('/auth/me');
      return response.data.data;
    } catch (error: any) {
      localStorage.removeItem('token');
      return rejectWithValue(error.response?.data?.message || 'Failed to get user data');
    }
  }
);

export const submitCompanyInquiry = createAsyncThunk(
  'auth/submitCompanyInquiry',
  async (inquiryData: {
    companyName: string;
    contactPersonName: string;
    email: string;
    phone: string;
    industry: string;
    companySize: string;
    website?: string;
    description: string;
    businessType: string;
    expectedBudget: number;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/company-inquiry', inquiryData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Company inquiry submission failed');
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send OTP');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async (data: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/verify-otp', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Invalid OTP');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (data: { email: string; otp: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/reset-password', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset password');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCredentials: (state, action: PayloadAction<{ user: AuthUser; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      })
      // Get Me
      .addCase(getMe.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        // Ensure token is set from localStorage
        if (!state.token) {
          state.token = localStorage.getItem('token');
        }
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(getMe.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = action.payload as string;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      });
  },
});

export const { clearError, setCredentials } = authSlice.actions;
export default authSlice.reducer;
